export type MeetingUser = {
  id?: string | number;
  userId?: string | number;
  uid?: string | number;
  rtcUid?: string | number;
  agoraUid?: string | number;
  shengwangUid?: string | number;
  rtcUserId?: string | number;
  clientId?: string | number;
  avatar?: string;
  portrait?: string;
  displayName?: string;
  nickName?: string;
  nickname?: string;
  name?: string;
  role?: string | number;
  userRole?: string | number;
  identity?: string | number | null;
  microphoneStatus?: string | number | boolean;
  micStatus?: string | number | boolean;
  muted?: boolean;
  extra?: string | {
    dndPledgeLevel?: {
      logo?: unknown;
    };
  } | null;
};

export function getUserKey(user: MeetingUser, index: number) {
  return user.id ?? user.userId ?? user.uid ?? `meeting-user-${index}`;
}

export function getUserRtcKeys(user: MeetingUser, index: number) {
  return [
    user.rtcUid,
    user.agoraUid,
    user.shengwangUid,
    user.rtcUserId,
    user.uid,
    user.userId,
    user.id,
    user.clientId,
    getUserKey(user, index),
  ]
    .filter((item) => item !== null && item !== undefined && item !== '')
    .map((item) => String(item));
}

export function isRtcUidMatched(
  user: MeetingUser,
  index: number,
  uids: string[],
) {
  if (!uids.length) return false;
  const userKeys = getUserRtcKeys(user, index);
  return userKeys.some((key) => uids.includes(key));
}

export function getDefaultAvatar(
  user: MeetingUser,
  index: number,
  defaultAvatars: string[],
) {
  const key = `${getUserKey(user, index)}`;
  let total = 0;
  for (let i = 0; i < key.length; i += 1) total += key.charCodeAt(i);
  return defaultAvatars[total % defaultAvatars.length];
}

export function getUserAvatar(
  user: MeetingUser,
  index: number,
  defaultAvatars: string[],
) {
  return user.avatar || user.portrait || getDefaultAvatar(user, index, defaultAvatars);
}

export function getUserName(user: MeetingUser) {
  return (
    user.displayName ||
    user.nickName ||
    user.nickname ||
    user.name ||
    ''
  );
}

export function getUserPledgeLogo(user: MeetingUser) {
  let extra = user.extra;

  if (typeof extra === 'string') {
    try {
      extra = JSON.parse(extra);
    } catch (error) {
      return '';
    }
  }

  if (!extra || typeof extra !== 'object') return '';

  const logo = extra.dndPledgeLevel?.logo;
  return typeof logo === 'string' && logo.trim() ? logo : '';
}

export function getUserIdentity(user: MeetingUser) {
  if (user.identity === null || user.identity === undefined || user.identity === '') {
    return 0;
  }

  const identity = Number(user.identity);
  return Number.isFinite(identity) ? identity : 0;
}

export function getRoleText(user: MeetingUser, index: number) {
  const identity = getUserIdentity(user);
  if (identity === 1) return '主持人';
  if (identity === 2) return '共同主持人';
  if (identity === 3) return '发言人';
  if (identity === 0) return '听众';

  const role = user.role ?? user.userRole;
  if (role === 1 || role === '1' || role === 'host') return '主持人';
  if (role === 2 || role === '2' || role === 'co_host') return '共同主持人';
  if (index < 1) return '主持人';
  if (index < 4) return '共同主持人';
  return '发言人';
}

export function isMuted(
  user: MeetingUser,
  index: number,
  unmutedRtcUids: string[],
) {
  if (isRtcUidMatched(user, index, unmutedRtcUids)) return false;

  const status = user.microphoneStatus ?? user.micStatus;
  if (getUserIdentity(user) === 0) return true;
  if (typeof user.muted === 'boolean') return user.muted;
  if (status === 0 || status === '0' || status === 'mute' || status === false) {
    return true;
  }
  if (
    status === 1 ||
    status === '1' ||
    status === 'unmute' ||
    status === 'open' ||
    status === 'on' ||
    status === true
  ) {
    return false;
  }
  return true;
}
